import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Users, 
  Calendar, 
  Clock,
  MapPin,
  GripVertical,
  X,
  CheckCircle,
  RotateCcw,
  FileText,
  FileDown,
  Search,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  History,
  Droplet,
  Printer
} from 'lucide-react';
import { clientsAPI, serviceCallsAPI, usersAPI } from '@shared/apiClient';
import { formatDate } from '@shared/utils';
import toast from 'react-hot-toast';
import { generateServiceCallPDF, printServiceCallPDF } from '../utils/pdfGenerator';
import CspireInvoiceEditor from '../components/CspireInvoiceEditor';
import CopyTextButton from '../components/CopyTextButton';

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white overflow-hidden shadow rounded-lg">
    <div className="p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="text-2xl font-semibold text-gray-900">{value}</dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
);

const ServiceCallCard = ({ serviceCall, onClick, onReassign, isDragging, onDragStart, onDragEnd, showCompletedDate = false }) => {
  const client = serviceCall.client;
  if (!client) return null;

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', serviceCall.id);
    onDragStart?.(serviceCall);
  };

  const visitCount = serviceCall.visits?.length || 0;
  const displayName = client.isCommercial && client.companyName 
    ? client.companyName 
    : `${client.contactFirstName} ${client.contactLastName}`;

  return (
    <div
      draggable={!!onDragStart}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`bg-white border-2 border-red-200 rounded-lg p-3 mb-2 cursor-pointer hover:border-red-400 hover:shadow-md transition-all ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start space-x-2">
        {onDragStart && <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
            <p className="text-sm font-medium text-gray-900 truncate">
              {displayName}
            </p>
            {visitCount > 0 && (
              <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded flex items-center gap-1">
                <History className="h-3 w-3" />
                {visitCount}
              </span>
            )}
          </div>
          {showCompletedDate && serviceCall.completedDate && (
            <p className="text-xs text-gray-500 mt-1">
              Completed: {formatDate(serviceCall.completedDate)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const TechnicianColumn = ({ technician, assignments, onDrop, onDragOver, isDraggingOver, onAssignmentDragStart, onAssignmentDragEnd, draggedAssignment }) => {
  const assignmentCount = assignments.length;

  return (
    <div
      className={`h-full flex flex-col bg-white rounded-lg border-2 shadow-sm ${
        isDraggingOver ? 'border-red-500 bg-red-50' : 'border-gray-200'
      } transition-colors`}
      onDrop={onDrop}
      onDragOver={onDragOver}
    >
      {/* Column Header */}
      <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200 px-3 py-2 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {technician.firstName} {technician.lastName}
            </h3>
            <p className="text-xs text-gray-500 truncate">{technician.email}</p>
          </div>
          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full flex-shrink-0 ml-2">
            {assignmentCount}
          </span>
        </div>
      </div>

      {/* Assignments List */}
      <div className="flex-1 p-2 overflow-y-auto min-h-0">
        {assignments.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">
            <p>Drop priority clients here</p>
          </div>
        ) : (
          assignments.map((assignment) => {
            const client = assignment.client;
            const cspireWorkOrder = assignment.cspireWorkOrder;
            const isCspire = assignment.isCspire && cspireWorkOrder;
            if (!client && !cspireWorkOrder) return null;
            
            const isDragging = draggedAssignment?.id === assignment.id;
            const displayName = isCspire
              ? `CSpire - WO# ${cspireWorkOrder.workOrderNumber || 'N/A'}`
              : (client.isCommercial && client.companyName 
                ? client.companyName 
                : `${client.contactFirstName} ${client.contactLastName}`);
            const location = isCspire
              ? (cspireWorkOrder.site?.city && cspireWorkOrder.site?.state
                ? `${cspireWorkOrder.site.city}, ${cspireWorkOrder.site.state}`
                : (cspireWorkOrder.locationName || cspireWorkOrder.site?.locationName || 'CSpire Site'))
              : `${client.city}, ${client.state}`;
            const isDraggable = !!client;
            
            return (
              <div
                key={assignment.id}
                draggable={isDraggable}
                onDragStart={(e) => {
                  if (!isDraggable) return;
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', assignment.id);
                  onAssignmentDragStart?.(assignment);
                }}
                onDragEnd={onAssignmentDragEnd}
                className={`bg-white border-2 border-red-200 rounded-lg p-3 mb-2 cursor-move hover:border-red-400 hover:shadow-md transition-all ${
                  isDragging ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {displayName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {location}
                    </p>
                    {isCspire && (
                      <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        CSpire
                      </span>
                    )}
                    {assignment.customerNotes && (
                      <p className="text-xs text-gray-400 mt-1 truncate">
                        {assignment.customerNotes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const ServiceCallViewModal = ({ serviceCall, isOpen, onClose, onFinalize }) => {
  const queryClient = useQueryClient();
  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false);

  const { data: serviceCallData, isLoading } = useQuery(
    ['serviceCall', serviceCall?.id],
    () => serviceCallsAPI.getServiceCall(serviceCall.id),
    { enabled: isOpen && !!serviceCall }
  );

  if (!isOpen || !serviceCall) return null;

  const fullServiceCall = serviceCallData?.data?.serviceCall || serviceCall;
  const client = fullServiceCall.client;
  const technician = fullServiceCall.technician;

  const handleDownloadPDF = () => {
    try {
      generateServiceCallPDF(fullServiceCall);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrintPDF = () => {
    try {
      printServiceCallPDF(fullServiceCall);
      toast.success('Print dialog opened');
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast.error('Failed to print PDF');
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 no-print">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10 print-content">
        <div className="flex items-center justify-between mb-4 print:mb-6">
          <h3 className="text-lg font-medium text-gray-900 print:text-2xl">Repair Service Call Details</h3>
          <div className="flex items-center space-x-2 no-print">
            {onFinalize && fullServiceCall.status === 'completed' && !fullServiceCall.isFinalized && (
              <button
                onClick={onFinalize}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                Mark as Finalized
              </button>
            )}
            {fullServiceCall.isCspire && (
              <button
                onClick={() => setShowInvoiceEditor(true)}
                className="px-3 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center space-x-1"
              >
                <FileText className="h-4 w-4" />
                <span>Edit Invoice</span>
              </button>
            )}
            <button
              onClick={handleDownloadPDF}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1"
            >
              <FileDown className="h-4 w-4" />
              <span>View PDF</span>
            </button>
            <button
              onClick={handlePrintPDF}
              className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center space-x-1"
            >
              <Printer className="h-4 w-4" />
              <span>Print</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 print:space-y-3">
          {/* Client Information */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-semibold text-gray-900 mb-2">Customer Information</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Company:</span> {client?.companyName || 'N/A'}
              </div>
              <div>
                <span className="text-gray-600">Contact:</span> {client?.contactFirstName} {client?.contactLastName}
              </div>
              <div>
                <span className="text-gray-600">Address:</span> {client?.address}
              </div>
              <div>
                <span className="text-gray-600">City, State:</span> {client?.city}, {client?.state} {client?.zipCode}
              </div>
              <div>
                <span className="text-gray-600">Phone:</span> {client?.phone}
              </div>
              <div>
                <span className="text-gray-600">Email:</span> {client?.email}
              </div>
              {(client?.generatorModel || client?.generatorSerialNumber || client?.generatorType || client?.generatorSize) && (
                <>
                  {client?.generatorType && (
                    <div>
                      <span className="text-gray-600">Generator Type:</span> {client.generatorType}
                    </div>
                  )}
                  {client?.generatorModel && (
                    <div>
                      <span className="text-gray-600">Generator Model:</span> {client.generatorModel}
                    </div>
                  )}
                  {client?.generatorSerialNumber && (
                    <div>
                      <span className="text-gray-600">Serial Number:</span> {client.generatorSerialNumber}
                    </div>
                  )}
                  {client?.generatorSize && (
                    <div>
                      <span className="text-gray-600">Generator Size:</span> {client.generatorSize}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Service Call Information */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-semibold text-gray-900 mb-2">Service Call Information</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Status:</span> {fullServiceCall.status}
              </div>
              <div>
                <span className="text-gray-600">Priority:</span> {fullServiceCall.priority || 'urgent'}
              </div>
              <div>
                <span className="text-gray-600">Technician:</span> {technician ? `${technician.firstName} ${technician.lastName}` : 'N/A'}
              </div>
              {fullServiceCall.scheduledDate && (
                <div>
                  <span className="text-gray-600">Scheduled:</span> {formatDate(fullServiceCall.scheduledDate)}
                </div>
              )}
              {fullServiceCall.completedDate && (
                <div>
                  <span className="text-gray-600">Completed:</span> {formatDate(fullServiceCall.completedDate)}
                </div>
              )}
              {fullServiceCall.isFinalized && (
                <div>
                  <span className="text-gray-600">Finalized:</span> <span className="text-green-600 font-medium">Yes</span>
                </div>
              )}
              {fullServiceCall.type && (
                <div>
                  <span className="text-gray-600">Type:</span> {fullServiceCall.type.name}
                </div>
              )}
              {fullServiceCall.isWarranty && (
                <div>
                  <span className="text-gray-600">Warranty:</span> <span className="text-blue-600 font-medium">Yes</span>
                </div>
              )}
            </div>
          </div>

          {/* Visit History */}
          {fullServiceCall.visits && fullServiceCall.visits.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex items-center gap-2 mb-3">
                <History className="h-5 w-5 text-yellow-700" />
                <h4 className="font-semibold text-gray-900">Visit History ({fullServiceCall.visits.length} visit{fullServiceCall.visits.length > 1 ? 's' : ''})</h4>
              </div>
              <div className="space-y-3">
                {fullServiceCall.visits.map((visit, idx) => (
                  <div key={visit.id} className="bg-white rounded-md p-3 border border-yellow-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">Visit #{visit.visitNumber}</span>
                      <span className="text-sm text-gray-600">{formatDate(visit.visitDate)}</span>
                    </div>
                    {visit.technician && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Technician:</span> {visit.technician.firstName} {visit.technician.lastName}
                      </p>
                    )}
                    {visit.type && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Type:</span> {visit.type.name}
                      </p>
                    )}
                    {visit.workPerformed && (
                      <div className="mb-2">
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-gray-700">Work Performed:</p>
                          <CopyTextButton text={visit.workPerformed} size="xs" />
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{visit.workPerformed}</p>
                      </div>
                    )}
                    {visit.outcome === 'needs_return' && visit.reasonForReturn && (
                      <div className="bg-yellow-50 rounded p-2 mt-2">
                        <p className="text-sm font-medium text-yellow-900 mb-1">Reason for Return:</p>
                        <p className="text-sm text-yellow-800">{visit.reasonForReturn}</p>
                      </div>
                    )}
                    {visit.partsUsed && visit.partsUsed.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700 mb-1">Parts Used:</p>
                        <ul className="text-sm text-gray-600 list-disc list-inside">
                          {visit.partsUsed.map((part, partIdx) => (
                            <li key={partIdx}>
                              {part.itemNumber || part.name} - Qty: {part.quantity} {part.cost && `($${parseFloat(part.cost).toFixed(2)})`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(visit.laborHours || visit.generatorHours || visit.mileage) && (
                      <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                        {visit.laborHours && (
                          <div>
                            <span className="text-gray-600">Labor Hours:</span> {visit.laborHours}
                          </div>
                        )}
                        {visit.generatorHours && (
                          <div>
                            <span className="text-gray-600">Generator Hours:</span> {visit.generatorHours}
                          </div>
                        )}
                        {visit.mileage && (
                          <div>
                            <span className="text-gray-600">Mileage:</span> {visit.mileage}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {fullServiceCall.description && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.description}</p>
            </div>
          )}

          {/* Work Performed */}
          {fullServiceCall.workPerformed && (
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="font-semibold text-gray-900">Work Performed</h4>
                <CopyTextButton text={fullServiceCall.workPerformed} />
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.workPerformed}</p>
            </div>
          )}

          {/* Parts Used */}
          {fullServiceCall.partsUsed && fullServiceCall.partsUsed.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Parts Used</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fullServiceCall.partsUsed.map((part, index) => {
                    const itemNumber = part.itemNumber || part.name || 'N/A';
                    const description = part.description || part.name || '';
                    const quantity = parseFloat(part.quantity) || 0;
                    const cost = parseFloat(part.cost) || 0;
                    const total = quantity * cost;
                    return (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{itemNumber}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{description}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{quantity.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">${cost.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">${total.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Labor, Cost, Hours & Mileage */}
          {(fullServiceCall.laborHours || fullServiceCall.totalCost || fullServiceCall.generatorHours || fullServiceCall.mileage) && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-semibold text-gray-900 mb-2">Service Metrics</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {fullServiceCall.laborHours && (
                  <div>
                    <span className="text-gray-600">Labor Hours:</span> {fullServiceCall.laborHours}
                  </div>
                )}
                {fullServiceCall.totalCost && (
                  <div>
                    <span className="text-gray-600">Total Cost:</span> ${parseFloat(fullServiceCall.totalCost).toFixed(2)}
                  </div>
                )}
                {fullServiceCall.generatorHours && (
                  <div>
                    <span className="text-gray-600">Generator Hours:</span> {parseFloat(fullServiceCall.generatorHours).toFixed(2)}
                  </div>
                )}
                {fullServiceCall.mileage && (
                  <div>
                    <span className="text-gray-600">Mileage:</span> {parseFloat(fullServiceCall.mileage).toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Next Service Date */}
          {fullServiceCall.nextServiceDate && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-semibold text-gray-900 mb-2">Next Service</h4>
              <div className="text-sm">
                <span className="text-gray-600">Scheduled for:</span> {formatDate(fullServiceCall.nextServiceDate)}
              </div>
            </div>
          )}

          {/* Customer Notes */}
          {fullServiceCall.customerNotes && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Customer Notes</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.customerNotes}</p>
            </div>
          )}

          {/* Internal Notes */}
          {fullServiceCall.internalNotes && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Internal Notes</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullServiceCall.internalNotes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
    <CspireInvoiceEditor
      serviceCall={fullServiceCall}
      isOpen={showInvoiceEditor}
      onClose={() => setShowInvoiceEditor(false)}
    />
    </>
  );
};

const AssignPriorityModal = ({ client, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    technicianId: '',
    notes: ''
  });

  // Reset form when modal closes
  if (!isOpen && formData.technicianId) {
    setFormData({ technicianId: '', notes: '' });
  }

  // Fetch technicians
  const { data: techniciansData } = useQuery(
    'technicians',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 }),
    { enabled: isOpen }
  );

  // Assignment mutation
  const assignMutation = useMutation(
    (data) => clientsAPI.assignPriorityServiceCall(data),
    {
      onSuccess: () => {
        toast.success('Repair service call assigned successfully!');
        queryClient.invalidateQueries('priorityAssignments');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign repair service call');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.technicianId) {
      toast.error('Please select a technician');
      return;
    }

    assignMutation.mutate({
      clientId: client.id,
      technicianId: formData.technicianId,
      notes: formData.notes
    });
  };

  if (!isOpen) return null;

  const technicians = techniciansData?.data?.users || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-red-50 px-4 py-5 sm:px-6 border-b border-red-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-red-900">
                  Assign Repair Service Call
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{client?.companyName}</span>
                  <span className="mx-2">•</span>
                  {client?.city}, {client?.state}
                </p>
              </div>
            </div>

            <div className="px-4 py-5 sm:p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Technician *
                </label>
                <select
                  value={formData.technicianId}
                  onChange={(e) => setFormData({ ...formData, technicianId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select a technician</option>
                  {technicians
                    .filter(tech => tech.isActive)
                    .map(tech => (
                      <option key={tech.id} value={tech.id}>
                        {tech.firstName} {tech.lastName} {tech.email}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any special instructions or notes..."
                />
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
              <button
                type="submit"
                disabled={assignMutation.isLoading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assignMutation.isLoading ? 'Assigning...' : 'Assign Repair Service'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default function PriorityServiceDashboard() {
  const [draggedAssignment, setDraggedAssignment] = useState(null);
  const [draggedReturnCall, setDraggedReturnCall] = useState(null);
  const [draggingOverColumn, setDraggingOverColumn] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [isServiceCallModalOpen, setIsServiceCallModalOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDockExpanded, setIsDockExpanded] = useState(true);

  const queryClient = useQueryClient();

  // Fetch technicians
  const { data: techniciansData } = useQuery(
    'technicians',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 })
  );

  // Fetch priority assignments (including both regular and priority for tech dock)
  const { data: priorityAssignmentsData } = useQuery(
    'priorityAssignments',
    () => clientsAPI.getPriorityAssignments(),
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  // Fetch all weekly assignments for tech dock
  const { data: weeklyAssignmentsData } = useQuery(
    'weeklyAssignments',
    () => clientsAPI.getWeeklyAssignments(),
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  // Fetch return service calls (priority only)
  const { data: returnServiceCallsData } = useQuery(
    'returnServiceCalls',
    () => serviceCallsAPI.getServiceCalls({ status: 'return', limit: 10000 }),
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  // Fetch completed (non-finalized) service calls (priority only)
  const { data: completedServiceCallsData } = useQuery(
    'completedServiceCalls',
    () => serviceCallsAPI.getServiceCalls({ status: 'completed', limit: 10000 }),
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  // Filter completed calls to separate finalized from non-finalized (priority only)
  const completedCalls = useMemo(() => {
    const calls = completedServiceCallsData?.data?.serviceCalls || [];
    return calls.filter(call => call.status === 'completed' && !call.isFinalized && call.isPriority);
  }, [completedServiceCallsData]);

  const finalizedCalls = useMemo(() => {
    const calls = completedServiceCallsData?.data?.serviceCalls || [];
    return calls.filter(call => call.status === 'completed' && call.isFinalized && call.isPriority);
  }, [completedServiceCallsData]);

  const returnCalls = useMemo(() => {
    const calls = returnServiceCallsData?.data?.serviceCalls || [];
    return calls.filter(call => call.isPriority);
  }, [returnServiceCallsData]);

  // Assignment mutation
  const assignMutation = useMutation(
    (data) => clientsAPI.assignPriorityServiceCall(data),
    {
      onSuccess: () => {
        toast.success('Repair service call assigned successfully!');
        queryClient.invalidateQueries('priorityAssignments');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign repair service call');
      }
    }
  );

  // Unassignment mutation
  const unassignMutation = useMutation(
    (serviceCallId) => clientsAPI.unassignPriorityServiceCall(serviceCallId),
    {
      onSuccess: () => {
        toast.success('Repair service call unassigned successfully!');
        queryClient.invalidateQueries('priorityAssignments');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to unassign repair service call');
      }
    }
  );

  // Finalize mutation
  const finalizeMutation = useMutation(
    (serviceCallId) => serviceCallsAPI.finalizeServiceCall(serviceCallId),
    {
      onSuccess: () => {
        toast.success('Repair service call finalized!');
        queryClient.invalidateQueries('completedServiceCalls');
        setIsServiceCallModalOpen(false);
        setSelectedServiceCall(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to finalize repair service call');
      }
    }
  );

  // Group assignments by technician (using all assignments for tech dock)
  const assignmentsByTechnician = useMemo(() => {
    const assignments = weeklyAssignmentsData?.data?.assignments || [];
    const grouped = {};
    
    assignments.forEach(assignment => {
      const techId = assignment.technicianId;
      if (!grouped[techId]) {
        grouped[techId] = [];
      }
      grouped[techId].push(assignment);
    });

    return grouped;
  }, [weeklyAssignmentsData]);

  const technicians = techniciansData?.data?.users?.filter(tech => tech.isActive) || [];
  const totalAssigned = (priorityAssignmentsData?.data?.assignments || []).length;

  const stats = [
    {
      title: 'Total Priority Assigned',
      value: totalAssigned,
      icon: Clock,
      color: 'text-red-600',
    },
    {
      title: 'Returns',
      value: returnCalls.length,
      icon: RotateCcw,
      color: 'text-yellow-600',
    },
    {
      title: 'Completed',
      value: completedCalls.length,
      icon: CheckCircle,
      color: 'text-green-600',
    },
  ];

  const handleAssignmentDragStart = (assignment) => {
    setDraggedAssignment(assignment);
    setDraggedReturnCall(null);
  };

  const handleReturnDragStart = (serviceCall) => {
    setDraggedReturnCall(serviceCall);
    setDraggedAssignment(null);
  };

  const handleDragEnd = () => {
    setDraggedAssignment(null);
    setDraggedReturnCall(null);
    setDraggingOverColumn(null);
  };

  const handleDragOver = (e, technicianId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggingOverColumn(technicianId);
  };

  const handleDrop = async (e, technicianId) => {
    e.preventDefault();
    setDraggingOverColumn(null);

    if (!technicianId || typeof technicianId !== 'string') {
      toast.error('Invalid technician selected');
      return;
    }

    // Handle return call reassignment
    if (draggedReturnCall) {
      const returnCall = draggedReturnCall;
      setDraggedReturnCall(null);

      const client = returnCall.client;
      if (!client || !client.id) {
        toast.error('Customer information is missing');
        return;
      }

      try {
        await serviceCallsAPI.updateServiceCall(returnCall.id, {
          status: 'assigned',
          technicianId: String(technicianId),
          isPriority: true // Ensure isPriority is true for priority service
        });
        toast.success('Priority return customer reassigned!');
        queryClient.invalidateQueries('returnServiceCalls');
        queryClient.invalidateQueries('priorityAssignments');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reassign priority return customer');
      }
      return;
    }

    // Handle reassignment (dragging from one tech to another)
    if (draggedAssignment) {
      const assignmentToReassign = draggedAssignment;
      setDraggedAssignment(null);

      const client = assignmentToReassign.client;
      if (!client || !client.id || typeof client.id !== 'string') {
        toast.error('Customer information is missing or invalid');
        return;
      }

      const requestData = {
        clientId: String(client.id),
        technicianId: String(technicianId)
      };

      try {
        await assignMutation.mutateAsync(requestData);
      } catch (error) {
        // Error handling is done in the mutation
      }
      return;
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await clientsAPI.getClients({ search: searchQuery, limit: 50 });
      setSearchResults(response.data.clients || []);
    } catch (error) {
      toast.error('Failed to search customers');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setIsAssignModalOpen(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleServiceCallClick = async (serviceCall) => {
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCall.id);
      setSelectedServiceCall(response.data.serviceCall);
      setIsServiceCallModalOpen(true);
    } catch (error) {
      toast.error('Failed to load service call details');
    }
  };

  const handleFinalize = async () => {
    if (selectedServiceCall && window.confirm('Mark this repair service call as finalized?')) {
      finalizeMutation.mutate(selectedServiceCall.id);
    }
  };

  const dockHeight = isDockExpanded ? 'h-64' : 'h-auto';

  return (
    <div className="flex h-full overflow-hidden">
      {/* Center Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Scrollable Main Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-6 space-y-6">
          {/* Header */}
          <div className="bg-white shadow rounded-lg p-3">
            <h1 className="text-2xl font-bold text-gray-900">Repair Service</h1>
            <p className="mt-1 text-sm text-gray-600">
              Emergency repairs, power outages, and urgent service requests
            </p>
          </div>

          {/* Client Search and Assign */}
          <div className="bg-white shadow rounded-lg p-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Assign Repair Service Call</h2>
            <div className="mb-4">
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search for customer by name, company, location, email, phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                {searchResults.map((client) => {
                  const displayName = client.isCommercial && client.companyName 
                    ? client.companyName 
                    : `${client.contactFirstName} ${client.contactLastName}`;
                  return (
                    <div
                      key={client.id}
                      onClick={() => handleClientSelect(client)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <p className="text-sm font-medium text-gray-900">{displayName}</p>
                      <p className="text-xs text-gray-500">
                        {client.city}, {client.state}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Finalized Block */}
          {finalizedCalls.length > 0 && (
            <div className="bg-white shadow rounded-lg p-4">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Finalized Repair Service Calls</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Technician
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completed Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {finalizedCalls.map((serviceCall) => {
                      const client = serviceCall.client;
                      const technician = serviceCall.technician;
                      const displayName = client?.isCommercial && client?.companyName 
                        ? client.companyName 
                        : `${client?.contactFirstName} ${client?.contactLastName}`;
                      return (
                        <tr key={serviceCall.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                              <div className="text-sm font-medium text-gray-900">
                                {displayName}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {technician ? `${technician.firstName} ${technician.lastName}` : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {serviceCall.completedDate ? formatDate(serviceCall.completedDate) : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleServiceCallClick(serviceCall)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              View Service Call
                            </button>
                            <button
                              onClick={() => {
                                // Could open a client details modal here
                                console.log('View customer:', client);
                              }}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              View Customer
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Footer with Technician Columns */}
        <div className={`flex-shrink-0 bg-white border-t-2 border-gray-300 transition-all duration-300 ${dockHeight}`}>
          <div className="h-full flex flex-col">
            {/* Tech Dock Header */}
            <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Tech Dock</h2>
                <button
                  onClick={() => setIsDockExpanded(!isDockExpanded)}
                  className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span>{isDockExpanded ? 'Collapse' : 'Expand'}</span>
                  {isDockExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Tech Dock - Collapsed and Expanded */}
            <div className="flex-1 overflow-hidden">
              <div className="h-full flex overflow-x-hidden">
                {technicians.map((technician) => {
                  const assignmentCount = (assignmentsByTechnician[technician.id] || []).length;
                  const assignments = assignmentsByTechnician[technician.id] || [];
                  
                  return (
                    <div
                      key={technician.id}
                      className={`flex-1 border-r border-gray-200 last:border-r-0 ${
                        draggingOverColumn === technician.id
                          ? 'bg-red-50'
                          : ''
                      } transition-colors min-w-0`}
                      onDrop={(e) => handleDrop(e, technician.id)}
                      onDragOver={(e) => handleDragOver(e, technician.id)}
                    >
                      {/* Technician Header - Always Visible */}
                      <div className={`px-3 py-2 border-b ${
                        draggingOverColumn === technician.id
                          ? 'border-red-300 bg-red-100'
                          : 'border-gray-200 bg-gray-50'
                      } cursor-pointer hover:bg-gray-100`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {technician.firstName}
                          </p>
                          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full flex-shrink-0">
                            {assignmentCount}
                          </span>
                        </div>
                      </div>

                      {/* Client List - Only in Expanded View */}
                      {isDockExpanded && (
                        <div className="overflow-y-auto h-[calc(100%-44px)] p-2">
                          {assignments.length === 0 ? (
                            <div className="text-center py-4 text-xs text-gray-400">
                              Drop here
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {assignments.map((assignment) => {
                                const client = assignment.client;
                                if (!client) return null;
                                
                                const isDragging = draggedAssignment?.id === assignment.id;
                                const isPriority = assignment.isPriority === true;
                                const currentMonth = new Date().getMonth() + 1;
                                const isOilChangeMonth = client.oilChangeMonth === currentMonth;
                                
                                return (
                                  <div
                                    key={assignment.id}
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('text/plain', assignment.id);
                                      handleAssignmentDragStart?.(assignment);
                                    }}
                                    onDragEnd={handleDragEnd}
                                    className={`bg-white border-2 rounded p-2 cursor-move hover:shadow-sm transition-all ${
                                      isDragging ? 'opacity-50' : 
                                      isPriority ? 'border-red-300 hover:border-red-500' : 'border-blue-300 hover:border-blue-500'
                                    }`}
                                  >
                                    <div className="flex items-start gap-1">
                                      {isPriority && <AlertTriangle className="h-3 w-3 text-red-600 flex-shrink-0 mt-0.5" />}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1">
                                          <p className="text-xs font-medium text-gray-900 truncate">
                                            {client.isCommercial && client.companyName 
                                              ? client.companyName 
                                              : `${client.contactFirstName} ${client.contactLastName}`}
                                          </p>
                                          {!isPriority && (
                                            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                                              Regular
                                            </span>
                                          )}
                                          {isOilChangeMonth && (
                                            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded flex items-center gap-0.5">
                                              <Droplet className="h-2.5 w-2.5" />
                                              Oil
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 truncate">
                                          {client.city}, {client.state}
                                        </p>
                                        {assignment.customerNotes && (
                                          <p className="text-xs text-gray-400 truncate mt-0.5">
                                            {assignment.customerNotes}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {technicians.length === 0 && (
                  <div className="flex-1 text-center py-8 text-gray-500">
                    No active technicians found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Fixed */}
      <aside className="w-80 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white">
        <div className="p-4 space-y-4">
            {/* Stats - Compact Version */}
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Statistics</h3>
              <div className="space-y-2">
                {stats.map((stat) => {
                  const Icon = stat.icon;
                  const displayValue = typeof stat.value === 'number' ? stat.value : (parseInt(stat.value) || 0);
                  return (
                    <div key={stat.title} className="flex items-center justify-between py-1">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <Icon className={`h-4 w-4 flex-shrink-0 ${stat.color}`} />
                        <span className="text-xs text-gray-600 truncate">{stat.title}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 ml-2">{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Return Block */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <RotateCcw className="h-5 w-5 text-yellow-600" />
                <h3 className="text-lg font-medium text-gray-900">Returns</h3>
                <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  {returnCalls.length}
                </span>
              </div>
              <div className="space-y-2">
            {returnCalls.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No priority return clients</p>
            ) : (
              returnCalls.map((serviceCall) => (
                <ServiceCallCard
                  key={serviceCall.id}
                  serviceCall={serviceCall}
                  onClick={() => handleServiceCallClick(serviceCall)}
                  onReassign={() => {}}
                  isDragging={draggedReturnCall?.id === serviceCall.id}
                  onDragStart={handleReturnDragStart}
                  onDragEnd={handleDragEnd}
                />
              ))
            )}
          </div>
        </div>

            {/* Completed Block */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-medium text-gray-900">Completed</h3>
                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                  {completedCalls.length}
                </span>
              </div>
              <div className="space-y-2">
            {completedCalls.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No completed repair service calls</p>
            ) : (
              completedCalls.map((serviceCall) => (
                <ServiceCallCard
                  key={serviceCall.id}
                  serviceCall={serviceCall}
                  onClick={() => handleServiceCallClick(serviceCall)}
                  showCompletedDate={true}
                />
              ))
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Assignment Modal */}
      {selectedClient && (
        <AssignPriorityModal
          client={selectedClient}
          isOpen={isAssignModalOpen}
          onClose={() => {
            setIsAssignModalOpen(false);
            setSelectedClient(null);
          }}
        />
      )}

      {/* Service Call View Modal */}
      <ServiceCallViewModal
        serviceCall={selectedServiceCall}
        isOpen={isServiceCallModalOpen}
        onClose={() => {
          setIsServiceCallModalOpen(false);
          setSelectedServiceCall(null);
        }}
        onFinalize={handleFinalize}
      />
    </div>
  );
}

